/**
 * Class ReadMore
 *
 * hide content of the container if it exceeds the character length limit
 */
define([
    'jquery',
    'handlebars',
    'mage/translate'
], (
    $,
    Handlebars
) => {
    class ReadMore {

        /**
         * Constructor
         *
         * @param {Object}  config  Configuration options passed in through JSON
         * @param {Element} element The element that this class was bound to
         *
         * @returns {null}
         */
        constructor(config, element) {
            const defaultConfig = {
                readMoreLessTemplateIDSelector: '#plp-text-read-more-less',
                contentContainerSelector: '[data-js=plp-text-content]',
                readMoreLessLinkSelector: '[data-js=read-more-less-link]',
                moreContentContainerSelector: '[data-js=more-content-container]',
                lessContentContainerSelector: '[data-js=less-content-container]',
                ellipsisText: '...',
                desktopBreakpoint: '1024',
                tabletBreakpoint: '768',
                desktopShowCharLength: 520,
                tabletShowCharLength: 420,
                mobileShowCharLength: 200,
                readMoreLabel: $.mage.__('Read More'),
                readLessLabel: $.mage.__('Read Less'),
                lessContentClass: 'plp-text-read-more-less__content--less',
                moreContentClass: 'plp-text-read-more-less__content--more'
            };
            // Merge default config options and provided config
            /** @type {Object} */
            this.config = { ...defaultConfig, ...config };
            /** @type {jQuery} */
            this.$element = $(element);

            this.readMoreLessTemplate = Handlebars.compile($(this.config.readMoreLessTemplateIDSelector).html());
            this.readMoreLabel = this.config.readMoreLabel;
            this.readLessLabel = this.config.readLessLabel;

            this.init();
        }

        /**
         * Initial read more component, if the target element has no content,
         * or content length is shorter than mobile show char number, we don't render
         * read more component
         */
        init() {
            const content = this.$element.html();
            if (!content || content.length < this.config.mobileShowCharLength) {
                return;
            }

            const windowInnerWidth = window.innerWidth;
            let showCharLength = this.config.mobileShowCharLength;

            if (windowInnerWidth >= this.config.desktopBreakpoint) {
                showCharLength = this.config.desktopShowCharLength;
            } else if (windowInnerWidth < this.config.desktopBreakpoint
                && windowInnerWidth >= this.config.tabletBreakpoint
            ) {
                showCharLength = this.config.tabletShowCharLength;
            }

            const lessContent = this.getLessContent(content, showCharLength);

            if (lessContent.length === 0) {
                return;
            }

            this.$element.html(this.readMoreLessTemplate({
                less_content: lessContent,
                ellipsis_text: this.config.ellipsisText,
                more_content: content,
                link_label: this.readMoreLabel
            }));

            $(this.config.contentContainerSelector).toggleClass(this.config.lessContentClass);

            this.attachListeners();
        }

        /**
         * Get content of less content container
         *
         * @param content
         * @param showCharLength
         * @returns {any}
         */
        getLessContent(content, showCharLength) {
            return content.length > showCharLength ? content.substr(0, showCharLength) : '';
        }

        /**
         * Attach event listeners to `Read More` element
         *
         * @returns {null}
         */
        attachListeners() {
            $(this.config.readMoreLessLinkSelector).on('click', (e) => {
                e.preventDefault();
                const $contentContainer = $(this.config.contentContainerSelector);
                const $readMoreLessLink = $(this.config.readMoreLessLinkSelector);
                const label = $contentContainer.hasClass(this.config.lessContentClass) ? this.readLessLabel : this.readMoreLabel;

                $readMoreLessLink.html(label);
                $contentContainer.toggleClass(this.config.lessContentClass).toggleClass(this.config.moreContentClass);
            });
        }

    }

    return (config, element) => {
        const readMore = new ReadMore(config, element);
        return readMore;
    };
});
